import { useEffect } from 'react';
import { getBetaAvailabilityKey, normalizeBetaSource } from '../../../utils/betaSources';
import { getRiskCommandsOrThrow } from '../orchestration/riskCommandRef';
import { RISK_INVALIDATION_CAUSES } from '../orchestration/useRiskOrchestrationCommands';

export default function useBetaContextRefresh({
    activeView,
    requestPosSizingContextUpdate,
    userUid,
    userData,
    bootstrapSelectionsReady = true,
    effectiveBetaContextKey,
    selectedBetaSource,
    selectedFactorModel,
    requestedFactorDataModel,
    markPendingRefreshFromLoadResult,
    riskCommandsRef,
    beginCoreLoadVisual,
    endCoreLoadVisual,
    betaSourceTransitionLoading,
    setBetaSourceTransitionLoading,
    betaSourceRefreshReadyRef,
    lastEffectiveBetaContextKeyRef,
    lastBetaRefreshDriverRef,
    betaContextRefreshRunIdRef,
    publishedBetaContextRef,
}) {
    useEffect(() => {
        if (!userUid || !userData) {
            return;
        }
        if (!bootstrapSelectionsReady) {
            return;
        }
        const nextEffectiveBetaContextKey = `${userUid}|${effectiveBetaContextKey}`;
        const nextBetaSource = normalizeBetaSource(selectedBetaSource);
        const nextFactorKey = `${selectedFactorModel}|${requestedFactorDataModel ?? ''}`;
        const publishedBetaContext = publishedBetaContextRef?.current;
        if (publishedBetaContext) {
            const publishedBetaSource = normalizeBetaSource(publishedBetaContext.betaSource);
            const durableUserBetaSource = normalizeBetaSource(userData?.beta_source);
            const durableAvailabilityKey = getBetaAvailabilityKey(
                userData?.available_beta_sources,
            );
            const durableContextMatchesPublication = (
                durableUserBetaSource === publishedBetaSource
                && durableAvailabilityKey === publishedBetaContext.availabilityKey
            );
            if (
                durableContextMatchesPublication
                && nextBetaSource !== publishedBetaSource
            ) {
                // Dashboard mirrors durable userData into selected state in a
                // following effect. Retain the acknowledgement until that
                // state catches up instead of consuming it one render early.
                return;
            }
            if (durableContextMatchesPublication && publishedBetaSource === nextBetaSource) {
                const commands = getRiskCommandsOrThrow(
                    riskCommandsRef,
                    'useBetaContextRefresh.publishedBetaContext',
                    ['refreshSelectedOverview'],
                );
                void requestPosSizingContextUpdate({
                    reason: 'beta_source_change',
                }).finally(() => {
                    commands.refreshSelectedOverview({ reason: 'beta_source_change' });
                });
                publishedBetaContextRef.current = null;
                betaSourceRefreshReadyRef.current = true;
                lastEffectiveBetaContextKeyRef.current = nextEffectiveBetaContextKey;
                lastBetaRefreshDriverRef.current = {
                    betaSource: nextBetaSource,
                    factorKey: nextFactorKey,
                };
                if (betaSourceTransitionLoading) {
                    setBetaSourceTransitionLoading(false);
                }
                return;
            }
            publishedBetaContextRef.current = null;
        }
        if (!betaSourceRefreshReadyRef.current) {
            betaSourceRefreshReadyRef.current = true;
            lastEffectiveBetaContextKeyRef.current = nextEffectiveBetaContextKey;
            lastBetaRefreshDriverRef.current = {
                betaSource: nextBetaSource,
                factorKey: nextFactorKey,
            };
            return;
        }

        if (lastEffectiveBetaContextKeyRef.current === nextEffectiveBetaContextKey) {
            lastBetaRefreshDriverRef.current = {
                betaSource: nextBetaSource,
                factorKey: nextFactorKey,
            };
            if (betaSourceTransitionLoading) {
                const commands = getRiskCommandsOrThrow(
                    riskCommandsRef,
                    'useBetaContextRefresh.transitionCleanup',
                    ['refreshSelectedOverview'],
                );
                void (async () => {
                    try {
                        await requestPosSizingContextUpdate({
                            reason: 'beta_source_change',
                        });
                    } finally {
                        setBetaSourceTransitionLoading(false);
                        commands.refreshSelectedOverview({ reason: 'beta_source_change' });
                    }
                })();
            }
            return;
        }

        const previousBetaRefreshDriver = lastBetaRefreshDriverRef.current;
        lastEffectiveBetaContextKeyRef.current = nextEffectiveBetaContextKey;
        lastBetaRefreshDriverRef.current = {
            betaSource: nextBetaSource,
            factorKey: nextFactorKey,
        };

        const factorModelDrivenChangeAlreadyHandled =
            previousBetaRefreshDriver?.betaSource === 'factor' &&
            nextBetaSource === 'factor' &&
            previousBetaRefreshDriver?.factorKey !== nextFactorKey;
        if (factorModelDrivenChangeAlreadyHandled) {
            return;
        }

        let cancelled = false;
        const refreshRunId = betaContextRefreshRunIdRef.current + 1;
        betaContextRefreshRunIdRef.current = refreshRunId;
        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'useBetaContextRefresh',
            [
                'invalidateRiskSurface',
                'refreshRiskSurface',
                'refreshSelectedOverview',
            ],
        );
        const coreLoadVisualToken = beginCoreLoadVisual();
        void (async () => {
            try {
                commands.invalidateRiskSurface({
                    cause: RISK_INVALIDATION_CAUSES.BETA_SOURCE_CHANGED,
                    reason: 'beta_source_change',
                });
                const loadResult = await commands.refreshRiskSurface({
                    reason: 'beta_context_change',
                    betaSource: nextBetaSource,
                    includeSelectedOverview: activeView === 'overview',
                    includeOptimizerSummaries: false,
                });
                if (cancelled) {
                    return;
                }

                markPendingRefreshFromLoadResult(loadResult);

                if (cancelled) {
                    return;
                }

                await requestPosSizingContextUpdate({
                    reason: 'beta_source_change',
                });
            } finally {
                endCoreLoadVisual(coreLoadVisualToken);
                if (betaContextRefreshRunIdRef.current === refreshRunId) {
                    setBetaSourceTransitionLoading(false);
                }
                // Also after a cancelled run: its successor may not reload,
                // and the command only requests what is out of date.
                commands.refreshSelectedOverview({ reason: 'beta_source_change' });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        activeView,
        requestPosSizingContextUpdate,
        beginCoreLoadVisual,
        betaContextRefreshRunIdRef,
        betaSourceRefreshReadyRef,
        betaSourceTransitionLoading,
        effectiveBetaContextKey,
        endCoreLoadVisual,
        lastBetaRefreshDriverRef,
        lastEffectiveBetaContextKeyRef,
        markPendingRefreshFromLoadResult,
        publishedBetaContextRef,
        requestedFactorDataModel,
        riskCommandsRef,
        selectedBetaSource,
        selectedFactorModel,
        setBetaSourceTransitionLoading,
        bootstrapSelectionsReady,
        userData,
        userUid,
    ]);
}
